import { Injectable } from '@angular/core';
import { logRequest } from '@office-start/telemetry-service';
import { KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';
import { filter, map, take } from 'rxjs/operators';

import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { ErrorDialogErrorCategory, ErrorDialogErrorType } from '../models/error-dialog.model';
import { ObservableScope } from '../models/observable-scope.enum';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { GuidGenerator } from '../utilities/guid-generator.utility';
import { createPopupDialog, listenForPopupClose } from '../utilities/popup-dialog.utility';
import { ScopableObservable, ScopableSubject } from '../utilities/scopable-subject.utility';
import { UrlParser } from '../utilities/url-parser.utility';
import { AccessToken, AccessTokenConsumer, AccessTokenResponseError } from './access-token.service';
import { DocumentService } from './document.service';
import { ErrorDialogService } from './error-dialog.service';
import { Performance } from './performance.service';
import { SessionStorageService } from './session-storage.service';
import { WindowService } from './window.service';

// This is also used in Website\msatoken\msa-token.ts. If making changes to this interface
// consider changing there as well.
interface TokenMessage {
    state?: string;
    token?: AccessToken;
    error?: string;
    message?: string;
}

const enum TokenFetchMode {
    Iframe = 'iframe',
    Popup = 'popup'
}

const enum TokenPromptMode {
    Login = 'login',
    None = 'none'
}

@Injectable({
    providedIn: 'root'
})
export class OfficeHomeMsaTokenService {

    private readonly _responses: ScopableSubject<TokenMessage> = new ScopableSubject();
    private readonly supportedTokens = new Map<AccessTokenConsumer, string>();
    private readonly handlerErrorId = 'handler_error';

    constructor(
        private readonly windowService: WindowService,
        private readonly documentService: DocumentService,
        private readonly contextService: ContextService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly sessionStorageService: SessionStorageService,
        private readonly instrumentationService: InstrumentationService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly errorDialogService: ErrorDialogService
    ) {
        this.init();
    }

    public supportsToken(tokenConsumer: AccessTokenConsumer) {
        return this.supportedTokens.has(tokenConsumer);
    }

    public requiresInteractiveLogin(tokenConsumer: AccessTokenConsumer): boolean {

        if (this.capabilitiesService.capabilities.isPwaInUwp) {
            return false;
        }

        // If the token is already in the cache, no need for a login popup.
        const cachedToken = this.getTokenFromCache(tokenConsumer);
        if (cachedToken && cachedToken.success && cachedToken.token && cachedToken.expiryMs > Date.now()) {
            return false;
        }

        // If login_required or interaction_required error, we need to force the user to login again through a popup.
        // For other errors, we will assume that token fetch will work non-interactively.
        return cachedToken && !cachedToken.success && this.isInteractiveErrorMessage(cachedToken.error?.message);
    }

    public fetchTokenInteractive(tokenConsumer: AccessTokenConsumer): Promise<AccessTokenResponse> {
        return new Promise<AccessTokenResponse>((resolve, reject) => {
            this.showLoginDialog(() => {
                const scope = this.supportedTokens.get(tokenConsumer);
                const state = this.getState(TokenFetchMode.Popup);
                const _window = this.windowService.nativeWindow;
                const popUpUrl = this.getTokenFetchUrl(scope, state, TokenPromptMode.Login);

                const startTime = Performance.now();
                const loginWindow = createPopupDialog(popUpUrl, _window);
                if (!loginWindow) {
                    const err = 'Could not create login popup';
                    this.logFetchRequest(false, true, false, -1, err);
                    reject(err);
                    return;
                }

                const popupChecker = listenForPopupClose(loginWindow, _window, () => {
                    const endTime = Performance.now();
                    const err = 'Popup closed by user';
                    this.logFetchRequest(false, true, false, Math.ceil(endTime - startTime), err);
                    reject(err);
                });

                this.waitForToken(tokenConsumer, state, startTime, false, true)
                    .catch()
                    .then(tokenResponse => {
                        _window.clearInterval(popupChecker);
                        loginWindow.close();
                        if (tokenResponse && tokenResponse.success) {
                            resolve(tokenResponse);
                        } else {
                            // Error is logged in waitForToken
                            reject('Failed to fetch token through popup');
                        }
                    });
            },
                () => {
                    const err = 'Verify dialog closed by user';
                    this.logFetchRequest(false, true, false, -1, err);
                    reject(err);
                });
        });
    }

    public getAccessTokenResponse(tokenConsumer: AccessTokenConsumer, allowInteractive = false, bypassCache: boolean = false): Promise<AccessTokenResponse> {
        if (!this.supportsToken(tokenConsumer)) {
            return Promise.reject('token not supported');
        }

        if (allowInteractive && this.requiresInteractiveLogin(tokenConsumer)) {
            return this.fetchTokenInteractive(tokenConsumer);
        }

        const startTime = Performance.now();
        if (!bypassCache) {
            const cachedToken = this.getTokenFromCache(tokenConsumer);
            if (cachedToken && cachedToken.success && cachedToken.token && cachedToken.expiryMs > Date.now()) {
                const endTime = Performance.now();
                this.logFetchRequest(true, false, true, Math.ceil(endTime - startTime));
                return Promise.resolve(cachedToken);
            }
        }

        const state = this.getState(TokenFetchMode.Iframe);
        const scope = this.supportedTokens.get(tokenConsumer);
        const iframeUrl = this.getTokenFetchUrl(scope, state, TokenPromptMode.None);
        const iframeId = this.getIframeId(tokenConsumer);
        const iframeElement = this.documentService.nativeDocument.getElementById(iframeId);
        if (!iframeElement) {
            this.documentService.addIframeToPage(iframeUrl, iframeId);
        } else {
            iframeElement.setAttribute('src', iframeUrl);
        }

        return this.waitForToken(tokenConsumer, state, startTime, true, false)
                   .then(r => {
                       // If the interactive error was not already in cache, then we will reach here.
                       // Trigger interactive flow if needed.
                       if (!r.success && allowInteractive && this.isInteractiveErrorMessage(r.error?.message)) {
                           return this.fetchTokenInteractive(tokenConsumer);
                       }

                       return r;
                   });
    }

    private waitForToken(tokenConsumer: AccessTokenConsumer, state: string, startTime: number, useTimeout: boolean, interactive: boolean): Promise<AccessTokenResponse> {
        let timeoutId = null;
        const promises: Array<Promise<AccessTokenResponse>> = [];

        if (useTimeout) {
            // It's possible the iframe postMessage never fires if login.live.com request has an error and doesn't redirect to /html/MsaToken.html.
            const timeout = new Promise<AccessTokenResponse>(resolve => {
                timeoutId = setTimeout(() => {
                    resolve({ success: false, token: null, format: AccessTokenFormat.RPSDelegation, error: new AccessTokenResponseError('timeout'), source: AccessTokenSource.MSA });
                }, this.contextService.sharedContext.msaTokenTimeoutMs);
            });

            promises.push(timeout);
        }

        const responsePromise = this.responses.withScope(ObservableScope.OnlyFuture).pipe(
            filter((response: TokenMessage) => response.state === state || response.state === this.handlerErrorId),
            take(1),
            map((response: TokenMessage): AccessTokenResponse => {
                if (!response || response.state === this.handlerErrorId || !response.token || !response.token.TokenValue || response.error) {
                    const error = new AccessTokenResponseError(InstrumentationService.filterPii(response.error)); // Not sure if error description can contain PII
                    return { success: false, token: null, format: AccessTokenFormat.RPSDelegation, error, source: AccessTokenSource.MSA };
                }

                return { success: true, token: response.token.TokenValue, format: AccessTokenFormat.RPSDelegation, source: AccessTokenSource.MSA, expiryMs: response.token.ExpiryMs };
            })
        ).toPromise();

        promises.push(responsePromise);

        return Promise.race(promises)
            .then(response => {
                const endTime = Performance.now();
                this.logFetchRequest(false, interactive, response.success, Math.ceil(endTime - startTime), response.error?.message);

                if (timeoutId) {
                    clearTimeout(timeoutId);
                }

                this.saveTokenToCache(tokenConsumer, response);
                return response;
            });
    }

    private getTokenFromCache(tokenConsumer: AccessTokenConsumer): AccessTokenResponse {
        const response = this.sessionStorageService.get(this.getCacheKey(tokenConsumer));
        try {
            const parsedError = JSON.parse(response.error);
            const error = new AccessTokenResponseError(parsedError.message, parsedError.statusCode);
            error.stack = parsedError.stack;
            return {
                ...response,
                error
            };
        } catch {
            return response;
        }
    }

    private saveTokenToCache(tokenConsumer: AccessTokenConsumer, tokenResponse: AccessTokenResponse) {
        const cacheableResponse = {
            ...tokenResponse,
            error: tokenResponse.error
                ? JSON.stringify({
                    message: tokenResponse.error?.message,
                    stack: tokenResponse.error.stack,
                    statusCode: tokenResponse.error.statusCode
                })
                : undefined
        };
        this.sessionStorageService.set(this.getCacheKey(tokenConsumer), cacheableResponse);
    }

    private getCacheKey(tokenConsumer: AccessTokenConsumer): string {
        // If changing this key, change the code in Logout.html to properly clear tokens from session storage.
        return `_msatoken_${tokenConsumer}`;
    }

    private isInteractiveErrorMessage(message: string): boolean {
        return message && (message.indexOf('login_required') !== -1 || message.indexOf('interaction_required') !== -1);
    }

    private get responses(): ScopableObservable<TokenMessage> {
        return this._responses.asScopableObservable();
    }

    private init() {
        if (!this.capabilitiesService.capabilities.msaClientTokensEnabled) {
            return;
        }

        this.windowService.nativeWindow.addEventListener('message', event => {
            const { origin, data } = event;

            if (this.windowService.sameOriginRegex.test(origin)) {
                this._responses.next(data);
            }
        });

        const isV2 = this.clientStartupConfigService.config.isV2Auth;
        if (!isV2 || !this.capabilitiesService.capabilities.graphApiForV2ConsumerEnabled) {
            this.supportedTokens.set(AccessTokenConsumer.OneDrive, 'onedrive_implicit.access');
            this.supportedTokens.set(AccessTokenConsumer.Graph, 'Family.Read Files.ReadWrite People.Read User.Read Contacts.Read');
        }
        // TODO: Move forms token fetch to commercial auth flow
        this.supportedTokens.set(AccessTokenConsumer.Forms, 'Forms.ReadWrite');

        // Create empty iframe elements on page load. This is to avoid perf cost of adding DOM elements when the token is requested.
        this.supportedTokens.forEach((_, token) => {
            this.documentService.addIframeToPage(null, this.getIframeId(token));
        });
    }

    private getIframeId(token: AccessTokenConsumer): string {
        return `ohpMSAToken_${token}`;
    }

    private getInstrumentationParams(fromCache: boolean, interactive: boolean): InstrumentationParams {
        const params = {};
        params[InstrumentationParam.State] = fromCache ? 'FromCache' : 'FromNetwork';
        params[InstrumentationParam.Action] = interactive ? 'interactive' : 'silent';
        return params;
    }

    private logFetchRequest(fromCache: boolean, interactive: boolean, success: boolean, latency: number, error?: string) {
        const params = this.getInstrumentationParams(fromCache, interactive);
        this.instrumentationService.logRequestEvent(KnownInstrumentationEvents.MsaTokenFetch, success ? 200 : 500, latency, error, fromCache, params);
        logRequest({ eventName: KnownRequestInstrumentationEvents.MsaTokenFetch, httpCode: success ? 200 : 500, latency, error, fromCache });
    }

    private getTokenFetchUrl(scope: string, state: string, prompt: TokenPromptMode): string {
        const appId = this.contextService.sharedContext.msaAppId;
        const redirectUrl = this.contextService.sharedContext.msaTokenRedirectUrl;
        const tokenRootUrl = this.contextService.sharedContext.inMockMode ? this.contextService.sharedContext.officeHomeApiRoot + `/getMsaToken?mockmode=${this.getMockMode()}&` : 'https://login.live.com/oauth20_authorize.srf?';
        return `${tokenRootUrl}client_id=${appId}` +
            '&response_type=token' +
            `&redirect_uri=${encodeURIComponent(redirectUrl)}` +
            '&response_mode=fragment' +
            `&scope=${encodeURIComponent(scope)}` +
            `&state=${encodeURIComponent(state)}` +
            `&prompt=${prompt}` +
            `&username=${encodeURIComponent(this.clientStartupConfigService.config.userPrincipalName)}`;
    }

    private getState(tokenFetchMode: TokenFetchMode): string {
        // requestId used as part of our state passed into login.live.com. It's important that this is random to avoid an attack where a postMessage is sent to our window with a 'malicious' token (like one that is for another user).
        const requestId = GuidGenerator.createGuid();
        return `${tokenFetchMode}|${requestId}`;
    }

    private showLoginDialog(onVerifyClick: () => void, onClose: () => void) {
        this.errorDialogService.showErrorDialog({ category: ErrorDialogErrorCategory.VerifyIdentity, error: ErrorDialogErrorType.LoginRequired, onActionClicked: onVerifyClick, onClose });
    }

    private getMockMode(): string {
        const params = UrlParser.getQueryParams(this.windowService.nativeWindow.location.search);
        return params['mockmsatoken'] || 'default';
    }
}
